/**
 * PWA update orchestration.
 *
 * SynFolio is a PWA (vite-plugin-pwa). Without explicit handling,
 * a freshly-deployed bundle sits in the browser's Service Worker
 * cache and the running tab keeps serving the OLD code until the
 * user manually hard-refreshes or unregisters the SW. The result:
 * Barrington deploys a fix, the workflow goes green, the live site
 * still shows yesterday's bundle.
 *
 * This module wires the contract that fixes that:
 *
 *  - Registers the SW manually so we get vite-plugin-pwa's
 *    onNeedRefresh / onRegisteredSW callbacks (the auto-injected
 *    register doesn't expose them).
 *  - Polls for new builds every 30 minutes while the tab is open
 *    and on every focus event — long-lived tabs catch fixes within
 *    half an hour, returning users get the latest immediately.
 *  - When an update is ready:
 *      hidden tab  → reload silently. There's no draft to lose
 *                    when the user isn't looking at the page.
 *      visible tab → dispatch `synfolio:update-available`. The
 *                    <UpdateAvailableBanner> renders a small
 *                    bottom toast with a Refresh button — the user
 *                    clicks when they're at a safe moment.
 *
 * The visible-tab path deliberately doesn't auto-reload because the
 * user might be typing into a comment, the plan-chat composer, or
 * the share-link composer; a silent reload would discard their work.
 */

import { registerSW } from 'virtual:pwa-register';

let updateSW: ((reload?: boolean) => Promise<void>) | null = null;

/** Idempotent — safe to call once at app boot from main.tsx. */
export function initPwaUpdate(): void {
  if (typeof window === 'undefined') return;
  if (updateSW) return; // already wired

  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      if (typeof document !== 'undefined' && document.hidden) {
        void applyUpdate();
        return;
      }
      window.dispatchEvent(new CustomEvent('synfolio:update-available'));
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Half-hourly background check. Cheap (304 most of the time)
      // and means a freshly-deployed fix is visible to long-lived
      // tabs within ~30 min without any user action.
      window.setInterval(
        () => {
          void registration.update();
        },
        30 * 60 * 1000,
      );
      // When the user comes back to the tab after a break, give the
      // SW a poke immediately. Most "I came back to my laptop after
      // an hour" cases are caught here, not by the interval.
      window.addEventListener('focus', () => {
        void registration.update();
      });
    },
  });
}

/**
 * Activate the waiting SW and reload the page.
 *
 * Called by the UpdateAvailableBanner's button. Falls back to a
 * plain reload if registerSW didn't return a controller (shouldn't
 * happen in practice — the banner only renders after we've
 * registered — but defence in depth).
 */
export async function applyUpdate(): Promise<void> {
  if (updateSW) {
    await updateSW(true);
    return;
  }
  window.location.reload();
}
